import { Layout } from "@/components/layout";
import {
  useGetNotificationPrefs,
  useUpdateNotificationPref,
  getGetNotificationPrefsQueryKey,
  type NotificationPref,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

export default function Notifications() {
  const qc = useQueryClient();
  const prefs = useGetNotificationPrefs();

  const update = useUpdateNotificationPref({
    mutation: {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: getGetNotificationPrefsQueryKey() });
      },
      onError: () => toast.error("Failed to update preference"),
    },
  });

  const rows = prefs.data ?? [];

  return (
    <Layout>
      <div className="flex-1 overflow-auto">
        <div className="max-w-2xl mx-auto p-6 space-y-4">
          <div>
            <h1 className="text-lg font-semibold">Notifications</h1>
            <p className="text-sm text-muted-foreground">
              Choose which hold alerts you receive as a direct Slack message when
              you own the affected listing's account. Turning one off here only
              stops your personal copy — the team channel alert still fires.
            </p>
          </div>

          {prefs.isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
            </div>
          ) : rows.length === 0 ? (
            <div className="border border-border rounded p-6 text-center text-sm text-muted-foreground">
              No notification preferences are available for your account.
            </div>
          ) : (
            <div className="border border-border rounded divide-y divide-border">
              {rows.map((p: NotificationPref) => (
                <div
                  key={p.alertType}
                  className="flex items-center justify-between px-4 py-3"
                >
                  <div>
                    <div className="text-sm font-medium">{p.label}</div>
                    <div className="text-xs text-muted-foreground">
                      Direct message when one of your listings is{" "}
                      {p.alertType === "hold_set" ? "put on hold" : "released"}.
                    </div>
                  </div>
                  <Switch
                    checked={!p.optedOut}
                    disabled={update.isPending}
                    onCheckedChange={(v) =>
                      update.mutate({
                        alertType: p.alertType,
                        data: { optedOut: !v },
                      })
                    }
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
